import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useState } from "react";
import ContainedButton from "../../../../../components/buttons/ContainedButton";
import TextButton from "../../../../../components/buttons/TextButton";

// Helper to format Date to datetime-local input value
const dateToLocalISOString = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const EmailEditor = ({
  email,
  isCreating,
  onSave,
  onClose,
  isSaving = false,
}) => {
  const [editedEmail, setEditedEmail] = useState(email);

  const handleFieldChange = (field, value) => {
    setEditedEmail((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayFieldChange = (field, value) => {
    const arrayValue = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setEditedEmail((prev) => ({ ...prev, [field]: arrayValue }));
  };

  const handleDateChange = (dateString) => {
    const newDate = dateString ? new Date(dateString) : null;
    setEditedEmail((prev) => ({ ...prev, scheduled_time: newDate }));
  };

  const handleFormatHTML = () => {
    if (!editedEmail.html) return;

    let formatted = editedEmail.html
      .replace(/></g, ">\n<")
      .replace(/^\s+|\s+$/gm, "");

    const lines = formatted.split("\n");
    let indentLevel = 0;
    const indentedLines = [];

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      if (trimmedLine.startsWith("</")) {
        indentLevel = Math.max(0, indentLevel - 1);
      }

      indentedLines.push("  ".repeat(indentLevel) + trimmedLine);

      if (
        trimmedLine.startsWith("<") &&
        !trimmedLine.startsWith("</") &&
        !trimmedLine.endsWith("/>") &&
        !["<br>", "<hr>", "<img", "<input", "<meta", "<link"].some((tag) =>
          trimmedLine.startsWith(tag)
        )
      ) {
        indentLevel++;
      }
    }

    handleFieldChange("html", indentedLines.join("\n"));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const value = e.target.value;
      const newValue = value.substring(0, start) + "  " + value.substring(end);
      handleFieldChange("html", newValue);
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
      }, 0);
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const start = e.target.selectionStart;
      const value = e.target.value;
      const lines = value.substring(0, start).split("\n");
      const currentLine = lines[lines.length - 1];
      const indent = currentLine.match(/^\s*/)[0];
      const newValue =
        value.substring(0, start) + "\n" + indent + value.substring(start);
      handleFieldChange("html", newValue);
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd =
          start + 1 + indent.length;
      }, 0);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "95%",
    maxWidth: 1400,
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: 2,
    p: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    overflow: "auto",
  };

  const editorStyle = {
    flex: 1,
    border: "1px solid #ccc",
    borderRadius: 1,
    position: "relative",
    backgroundColor: "#1e1e1e",
    fontFamily: "monospace",
    display: "flex",
  };

  const textareaStyle = {
    flex: 1,
    border: "none",
    outline: "none",
    resize: "none",
    padding: "12px",
    fontFamily: "Monaco, 'Cascadia Code', 'Roboto Mono', monospace",
    fontSize: "14px",
    lineHeight: "1.5",
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    tabSize: 2,
    whiteSpace: "pre",
    overflowWrap: "normal",
    overflowX: "auto",
    overflowY: "auto",
  };

  return (
    <Modal open={true} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6">
          {isCreating
            ? "Create New Email Notification"
            : "Edit Email Notification"}
        </Typography>

        {/* Basic Fields */}
        <Box display="flex" flexDirection="column" gap={2}>
          {isCreating && (
            <TextField
              label="Customer ID"
              value={editedEmail.customer_id || ""}
              onChange={(e) => handleFieldChange("customer_id", e.target.value)}
              fullWidth
              required
            />
          )}
          <TextField
            label="Subject"
            value={editedEmail.subject || ""}
            onChange={(e) => handleFieldChange("subject", e.target.value)}
            fullWidth
          />
          <Box display="flex" gap={2}>
            <TextField
              label="To Addresses (comma-separated)"
              value={editedEmail.to_addresses?.join(", ") || ""}
              onChange={(e) =>
                handleArrayFieldChange("to_addresses", e.target.value)
              }
              fullWidth
            />
            <TextField
              label="BCC Addresses (comma-separated)"
              value={editedEmail.bcc_addresses?.join(", ") || ""}
              onChange={(e) =>
                handleArrayFieldChange("bcc_addresses", e.target.value)
              }
              fullWidth
            />
          </Box>
          <TextField
            label="Scheduled Time"
            type="datetime-local"
            value={dateToLocalISOString(editedEmail.scheduled_time)}
            onChange={(e) => handleDateChange(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* HTML Editor and Preview */}
        <Typography variant="h6">HTML Content Editor & Preview</Typography>
        <Box display="flex" gap={2} height="500px">
          {/* HTML Editor */}
          <Box flex={1} display="flex" flexDirection="column">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="subtitle2">HTML Source Code</Typography>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleFormatHTML}
                >
                  Format
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleFieldChange("html", "")}
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <Box sx={editorStyle}>
              {/* Line numbers */}
              <Box
                sx={{
                  width: "50px",
                  backgroundColor: "#252526",
                  borderRight: "1px solid #3e3e42",
                  padding: "12px 8px",
                  fontSize: "12px",
                  color: "#858585",
                  fontFamily:
                    "Monaco, 'Cascadia Code', 'Roboto Mono', monospace",
                  lineHeight: "1.5",
                  textAlign: "right",
                  userSelect: "none",
                  overflow: "hidden",
                }}
              >
                {(editedEmail.html || "").split("\n").map((_, index) => (
                  <div key={index} style={{ height: "21px" }}>
                    {index + 1}
                  </div>
                ))}
              </Box>

              {/* Code editor textarea */}
              <textarea
                value={editedEmail.html || ""}
                onChange={(e) => handleFieldChange("html", e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter HTML content here..."
                style={textareaStyle}
              />
            </Box>
          </Box>

          {/* Preview */}
          <Box flex={1} display="flex" flexDirection="column">
            <Typography variant="subtitle2" mb={1}>
              Live Preview
            </Typography>
            <Box
              sx={{
                flex: 1,
                border: "1px solid #ccc",
                borderRadius: 1,
                overflow: "auto",
                backgroundColor: "#fff",
                p: 2,
              }}
            >
              {editedEmail.html ? (
                <div
                  dangerouslySetInnerHTML={{ __html: editedEmail.html }}
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.5",
                    color: "#333",
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ fontStyle: "italic" }}
                >
                  HTML preview will appear here as you type...
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" gap={2} mt={3}>
          <TextButton onClick={onClose}>Cancel</TextButton>
          <ContainedButton
            onClick={() => onSave(editedEmail)}
            color="primary"
            size="medium"
            loading={isSaving}
          >
            {isCreating ? "Create Email" : "Save Changes"}
          </ContainedButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default EmailEditor;
